/**
 * AI-generated code, moderated by Lukasz Zygmanski
 */
import {CircleHelp, FileText} from 'lucide-react';
import {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Link} from 'react-router-dom';

import {Badge} from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {IconButton} from '@/components/ui/icon-button';
import {useAuthUser} from '@/hooks/useAuth';
import {
  useHasNewVisibleReleaseNotes,
  useVisibleReleaseNotes,
} from '@/shared/release-notes/hooks/useVisibleReleaseNotes';
import {markVisibleReleaseNotesAsSeen} from '@/shared/release-notes/utils/releaseNoteUtils';

function HelpDropdown() {
  const {t} = useTranslation();
  const {data: authUser} = useAuthUser();
  const userId = authUser?.userId || authUser?.username;
  const {releaseNotesReady, visibleReleaseNoteIds} = useVisibleReleaseNotes();
  const hasUnseenReleaseNotes = useHasNewVisibleReleaseNotes();
  const [hasAcknowledgedReleaseNotes, setHasAcknowledgedReleaseNotes] =
    useState(false);
  const hasNewReleaseNotes =
    hasUnseenReleaseNotes && !hasAcknowledgedReleaseNotes;

  useEffect(() => {
    setHasAcknowledgedReleaseNotes(false);
  }, [userId, visibleReleaseNoteIds]);

  const handleOpenChange = (open: boolean) => {
    if (
      open ||
      !hasNewReleaseNotes ||
      !releaseNotesReady ||
      !userId ||
      !visibleReleaseNoteIds.length
    ) {
      return;
    }

    markVisibleReleaseNotesAsSeen(userId, visibleReleaseNoteIds);
    setHasAcknowledgedReleaseNotes(true);
  };

  return (
    <DropdownMenu onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild>
        <IconButton
          className="relative shrink-0 rounded-full bg-white shadow-subtle"
          variant="tertiary"
          size="default"
        >
          <CircleHelp />
          {hasNewReleaseNotes && (
            <span className="absolute top-1 right-1 flex size-2.5 items-center justify-center">
              <span className="absolute -inset-1 animate-ping rounded-full bg-marian-600 opacity-75" />
              <span className="relative inline-flex size-2.5 rounded-full bg-marian-600 ring-2 ring-white" />
            </span>
          )}
          <span className="sr-only">{t('Help')}</span>
        </IconButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent side="bottom" align="end">
        <DropdownMenuItem asChild>
          <Link className="flex items-center gap-2" to="/releases">
            <FileText size={16} />
            <span>{t('Release notes')}</span>
            {hasNewReleaseNotes && (
              <Badge
                color="secondary"
                size="small"
                variant="filled"
                className="ml-auto"
              >
                {t('New')}
              </Badge>
            )}
          </Link>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default HelpDropdown;
